import { Badge, Tooltip } from 'antd';
import { WorkspaceMembers } from '@/types/generated/graphql';
import { getDotColor } from '@modules/members/utils/get-dot-color';
import { useProfile } from '@components/profile-dropdown/hooks/use-profile-dropdown';
import { useScreenSize } from '@shared/hooks/use-screen';
import { useGetWorkspaceTimezone } from '@shared/hooks/use-get-workspace-timezone';
import { AppAvatar } from '@components/app-avatar';
import { IconCrown } from '@/assets/static/icons';
import { RiShieldUserLine } from 'react-icons/ri';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { truncateString } from '@modules/settings/subscription/utils/utils';

export function MemberCard(member: WorkspaceMembers) {
  const { isMobile } = useScreenSize();
  const { transformDateFormat } = useGetWorkspaceTimezone();
  const { icon, message, awayTill } = member?.userOnlineManagement || {};
  const { loggedInUserId } = useProfile();
  const showLabel =
    member?.user?.id === loggedInUserId ? 'YOU' : member?.roles?.[0]?.label === 'Owner' ? 'OWNER' : null;
  const formattedDate = transformDateFormat(awayTill, 'MMM D, YYYY, hh:mm A');
  const isActive = member.userOnlineManagement?.isActive;
  const role = member?.roles?.[0]?.label;
  const { t } = useTranslation();

  return (
    <div key={member?.id} className="flex gap-2.5 items-center h-9">
      <Badge dot style={{ backgroundColor: getDotColor(isActive) }} offset={isMobile ? [-5, 24] : [-6, 31]}>
        <AppAvatar
          className="shrink-0 size-7! lg:size-9! border-gray-500!"
          src={member?.user?.profilePicture?.url}
          name={member?.user?.name}
        />
      </Badge>
      <div className="w-full overflow-hidden leading-4">
        <p className="flex items-center">
          <span className="flex gap-2 leading-5 text-[15px] lg:text-base text-text-700 group-[.ant-menu-item-selected]:text-text-800">
            {truncateString(member?.user?.name, 11)}
            {member?.roles?.[0]?.label === 'Owner' && <IconCrown />}
            {member?.roles?.[0]?.label === 'Admin' && <RiShieldUserLine size={20} className=" text-primary-400" />}
          </span>
          <Tooltip
            title={
              <p className="flex flex-col">
                <span className="text-xs font-medium text-[13px] text-slate-950">{message}</span>
                <span className="font-normal text-slate-600 text-xs capitalize!">
                  {t('until_date', { date: formattedDate })}
                </span>
              </p>
            }
            placement="bottom"
          >
            <span>{icon}</span>
          </Tooltip>
        </p>
        {showLabel ? (
          <p className="truncate text-xs leading-4.5 text-text-600 group-[.ant-menu-item-selected]:text-text-700 tracking-[0.04em]">
            <span>{showLabel}</span>
          </p>
        ) : (
          <p className="truncate text-xs leading-4.5 text-text-600 group-[.ant-menu-item-selected]:text-text-700 tracking-[0.04em] uppercase">
            <span>{role}</span>
          </p>
        )}
      </div>
      <div
        className={clsx(
          'flex flex-1 justify-end items-center text-sm group-hover:animate-disappear-bottom-xxs',
          isMobile && 'group-focus:animate-disappear-bottom-xxs',
          !member?.unreadCounter && 'pr-1'
        )}
      >
        {member?.unreadCounter ? <Badge count={member?.unreadCounter} style={{ boxShadow: 'unset' }} /> : undefined}
      </div>
    </div>
  );
}
